import { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'

import { formatDate, timeAgo } from 'lib/date'

import Table, { getExpanderCell } from 'backoffice/components/table/Table'
import Card from 'backoffice/components/DropRequests/Card'
import ListMenuButton from 'backoffice/components/DropRequests/ListMenuButton'
import StaticFilter from 'backoffice/components/DropRequests/Filters/StaticFilter'
import DropSearchbox from 'backoffice/components/DropRequests/DropSearchbox'
import ConfirmationModal from 'backoffice/components/DropRequests/Modals/ConfirmationModal/ConfirmationModal'
import DraftSearchCard from 'backoffice/components/DropRequests/DraftSearchCard'
import DraftIndicator from 'backoffice/components/DropRequests/DraftIndicator'
import { useGetPetitionsQuery } from './usePetitionsQuery'
import { useSearchDraftByDropId, useBatchDraftsForPetitions, DraftsMapContext } from './useDraftForPetition'
import { removeProp } from 'api/fetch'

import { Status, DistributionMethods, DistributionLabels } from './types/types'
import { ROUTES } from 'lib/constants'
import { copyToClipboard } from 'lib/utils'

import { ReactComponent as UserIcon } from 'images/icons/user-circle.svg'
import { ReactComponent as ClockIcon } from 'images/icons/clock.svg'
import { ReactComponent as BoxIcon } from 'images/icons/box.svg'
import { ReactComponent as LightningIcon } from 'images/icons/lightning.svg'
import { ReactComponent as CheckIcon } from 'images/icons/check-circle.svg'
import { ReactComponent as TimesIcon } from 'images/icons/times-circle.svg'
import { ReactComponent as FlagIcon } from 'images/icons/flag-circle.svg'
import { ReactComponent as CopyIcon } from 'images/icons/copy.svg'

import styles from './List.module.scss'

const STATUS_OPTIONS = [
  { value: Status.PENDING, label: 'Pending' },
  { value: Status.APPROVED, label: 'Approved' },
  { value: Status.FLAGGED, label: 'Flagged' },
  { value: Status.REJECTED, label: 'Rejected' },
]

const METHOD_OPTIONS = [
  { value: DistributionMethods.MINT_LINK, label: DistributionLabels.MINT_LINK },
  { value: DistributionMethods.WEBSITE, label: DistributionLabels.WEBSITE },
  { value: DistributionMethods.DELIVERY, label: DistributionLabels.DELIVERY },
  { value: DistributionMethods.SECRET, label: DistributionLabels.SECRET },
]

function getMethodName(method: DistributionMethods): DistributionLabels {
  var methodLabels = {
    [DistributionMethods.MINT_LINK]: DistributionLabels.MINT_LINK,
    [DistributionMethods.WEBSITE]: DistributionLabels.WEBSITE,
    [DistributionMethods.DELIVERY]: DistributionLabels.DELIVERY,
    [DistributionMethods.SECRET]: DistributionLabels.SECRET,
  }

  return methodLabels[method]
}

const PAGINATION_OPTIONS = [10, 50, 100, 1000]
const DEFAULT_LIMIT = PAGINATION_OPTIONS[0]

const DropRequests = () => {
  const { addToast } = useToasts()

  const { petitionsQuery, setQueryParams, currentDropId } = useGetPetitionsQuery({
    page: '1',
    limit: DEFAULT_LIMIT.toString(),
  })

  // Extract all drop_ids from the current page of petitions for batch draft fetching
  const petitionDropIds = useMemo(() => {
    const results = petitionsQuery.data?.results ?? []
    return results.map((p) => p.drop_id).filter((id) => id != null)
  }, [petitionsQuery.data])

  const { data: draftsMap = new Map(), isLoading: isDraftsLoading } = useBatchDraftsForPetitions(petitionDropIds)
  const draftsContextValue = useMemo(() => ({ draftsMap, isLoading: isDraftsLoading }), [draftsMap, isDraftsLoading])

  const { data: draftForDrop, isLoading: isDraftLoading } = useSearchDraftByDropId(
    currentDropId ? parseInt(currentDropId, 10) : null,
  )

  const handleCopy = useCallback(
    /* @ts-ignore TODO: remove ignore TS validation */
    (text) => {
      copyToClipboard(text)
      addToast(`Email Copied!`, {
        appearance: 'success',
        autoDismiss: true,
      })
    },
    [addToast],
  )

  const columns = useMemo(
    () => [
      getExpanderCell(),
      {
        Header: 'Req. ID',
        accessor: 'id',
        className: styles.idField,
        isExpandable: true,
      },
      {
        Header: 'Drop',
        id: 'eventId',
        className: styles.eventIdField,
        Cell: ({ row: { original } }: any) => {
          const { fancy_id, id, name } = original.drop
          return (
            <div
              className={styles.cellContentWrapper}
              aria-label={`#${id} - ${name}`}
              data-cooltipz-dir="top"
              data-cooltipz-size="large"
            >
              <Link to={`${ROUTES.event.path}${fancy_id}`} className={styles.eventName}>{`#${id} - ${name}`}</Link>
              <DraftIndicator dropId={original.drop_id} />
            </div>
          )
        },
      },
      {
        Header: 'Issuer',
        accessor: 'issuer',
        isExpandable: false,
        Cell: ({ cell }: any) => (
          <>
            <UserIcon className={styles.icon} />
            <div className={styles.emailContainer} onClick={() => handleCopy(cell.value)}>
              <span className={styles.email} title={cell.value}>
                {cell.value}
              </span>
              <CopyIcon className={styles.copy} />
            </div>
          </>
        ),
      },
      {
        Header: 'Date Created',
        accessor: 'created_at',
        isExpandable: true,
        Cell: ({ cell }: any) => {
          return (
            <div
              className={cn(styles.createdAt, styles.cellContentWrapper)}
              aria-label={formatDate(new Date(cell.value), 'd-MMM-yyyy HH:mm')}
              data-cooltipz-dir="top"
            >
              <ClockIcon className={styles.icon} />
              <span>{timeAgo(cell.value)}</span>
            </div>
          )
        },
      },
      {
        Header: 'Size',
        id: 'size',
        isExpandable: true,
        Cell: ({ row: { original } }: any) => {
          return (
            <>
              <BoxIcon className={styles.icon} />
              <span>{`${original.amount_approved ?? 0}/${original.amount_requested}`}</span>
            </>
          )
        },
      },
      {
        Header: 'Method',
        accessor: 'distribution_method',
        isExpandable: true,
        Cell: ({ cell }: any) => (
          <>
            <LightningIcon className={styles.icon} />
            <span className={styles.distributionField}>{getMethodName(cell.value)}</span>
          </>
        ),
      },
      {
        Header: 'Status',
        accessor: 'status',
        className: styles.statusCell,
        isExpandable: true,
        Cell: ({ cell }: any) => (
          <>
            {cell.value === Status.APPROVED && (
              <span aria-label="Approved" data-cooltipz-dir="top">
                <CheckIcon />
              </span>
            )}
            {cell.value === Status.FLAGGED && (
              <span aria-label="Flagged" data-cooltipz-dir="top">
                <FlagIcon />
              </span>
            )}
            {cell.value === Status.REJECTED && (
              <span aria-label="Rejected" data-cooltipz-dir="top">
                <TimesIcon />
              </span>
            )}
          </>
        ),
      },
      {
        id: 'edit',
        className: styles.editCell,
        Cell: ({ row: { original } }: any) => (
          <ListMenuButton petition={original} className={styles.cellContentWrapper} />
        ),
      },
    ],
    [handleCopy],
  )

  const handleSearchOption = useCallback(
    // TODO: remove "removeProp" when the API handle empty params correctly
    (dropId: string) =>
      setQueryParams((prev) => {
        const queryParams = dropId ? { ...prev, drop_id: dropId } : removeProp(prev, 'drop_id')
        return { ...queryParams, page: '1' }
      }),

    [setQueryParams],
  )

  const handleStatusOption = useCallback(
    // TODO: remove "removeProp" when the API handle empty params correctly
    (status: string) =>
      setQueryParams((prev) => {
        const queryParams = status ? { ...prev, status } : removeProp(prev, 'status')
        return { ...queryParams, page: '1' }
      }),
    [setQueryParams],
  )

  const handleMethodOption = useCallback(
    (method: string) =>
      setQueryParams((prev) => {
        // TODO: remove "removeProp" when the API handle empty params correctly
        const queryParams = method ? { ...prev, distribution_method: method } : removeProp(prev, 'distribution_method')
        return { ...queryParams, page: '1' }
      }),
    [setQueryParams],
  )

  /* @ts-ignore TODO: remove ignore TS validation */
  const renderCard = useCallback(({ row }) => <Card petition={row.original} />, [])

  return (
    <DraftsMapContext.Provider value={draftsContextValue}>
      <div className={styles.filters}>
        <DropSearchbox onOptionSelected={handleSearchOption} />

        <StaticFilter
          options={STATUS_OPTIONS}
          id="status"
          isCleareable
          placeholder="Status"
          onChange={handleStatusOption}
        />

        <StaticFilter
          options={METHOD_OPTIONS}
          id="method"
          isCleareable
          placeholder="Method"
          onChange={handleMethodOption}
        />
      </div>

      {currentDropId && (
        <div className={styles.draftSearchResults}>
          {isDraftLoading && (
            <div className={styles.draftSearchLoading}>
              <div className={styles.draftSearchSpinner} />
              <span>Checking for console draft petition...</span>
            </div>
          )}

          {draftForDrop && (
            <>
              <div className={styles.draftResultsHeader}>
                <h3 className={styles.draftResultsTitle}>Console Draft for Drop #{currentDropId}</h3>
              </div>
              <DraftSearchCard draft={draftForDrop} />
            </>
          )}
        </div>
      )}

      <Table
        columns={columns}
        query={petitionsQuery}
        setQueryParams={setQueryParams}
        renderRowSubComponent={renderCard}
        className={styles.table}
        resultsPerPage={PAGINATION_OPTIONS}
      />

      <ConfirmationModal />
    </DraftsMapContext.Provider>
  )
}

export default DropRequests
